import { findProfessorRatings } from '@/app/lib/professorData';
import type { ExploreCatalogSummary, ExploreCourseData, RutgersCourse } from '@/app/types/interfaces';

/** Shared detail model for the Explore route and embedded course inspectors. */
export function toExploreCourseData(summary: ExploreCatalogSummary, sections: RutgersCourse[]): ExploreCourseData {
  const professorRatings = findProfessorRatings(summary.instructors)
    .filter((professor, index, all) => all.findIndex((entry) => entry.id === professor.id) === index);
  const average = (field: 'avgRating' | 'avgDifficulty' | 'wouldTakeAgainPercent') => professorRatings.length
    ? professorRatings.reduce((total, professor) => total + professor[field], 0) / professorRatings.length
    : 0;
  const enrichedSections = sections.map((section) => {
    const sectionRatings = findProfessorRatings(section.instructors);
    return {
      ...section,
      professorRatings: sectionRatings,
      averageRating: sectionRatings.length ? sectionRatings.reduce((total, professor) => total + professor.avgRating, 0) / sectionRatings.length : 0,
      averageDifficulty: sectionRatings.length ? sectionRatings.reduce((total, professor) => total + professor.avgDifficulty, 0) / sectionRatings.length : 0,
      averageWouldTakeAgain: sectionRatings.length ? sectionRatings.reduce((total, professor) => total + professor.wouldTakeAgainPercent, 0) / sectionRatings.length : 0,
      totalRatings: sectionRatings.reduce((total, professor) => total + professor.numRatings, 0),
    };
  });

  return {
    baseId: summary.baseId,
    courseName: summary.courseName,
    fullTitle: summary.fullTitle || summary.courseName,
    department: summary.department,
    class_number: summary.class_number,
    credits: summary.credits,
    campus: summary.exampleSection?.campus || summary.campuses?.[0] || 'Unknown',
    campuses: summary.campuses,
    instructors: summary.instructors,
    meetingTypes: summary.meetingTypes,
    schedulePatterns: summary.schedulePatterns,
    schedulePatternCount: summary.schedulePatternCount,
    instructorProfiles: summary.instructorProfiles,
    classroomCapacity: summary.classroomCapacity,
    aiInsight: summary.aiInsight,
    coreRequirements: summary.coreRequirements || [],
    sections: enrichedSections,
    professorRatings,
    averageRating: average('avgRating'),
    averageDifficulty: average('avgDifficulty'),
    averageWouldTakeAgain: average('wouldTakeAgainPercent'),
    totalRatings: professorRatings.reduce((total, professor) => total + professor.numRatings, 0),
    hasOpenSections: summary.openSectionsCount > 0,
    openSectionsCount: summary.openSectionsCount,
    totalSectionsCount: summary.totalSectionsCount,
    synopsisUrl: sections[0]?.synopsisUrl,
    courseNotes: sections[0]?.courseNotes,
    unitNotes: sections[0]?.unitNotes,
    subjectNotes: sections[0]?.subjectNotes,
    subjectGroupNotes: sections[0]?.subjectGroupNotes,
  };
}
